import { useState } from "react";
import AppModal from "../../../shared/components/app-modal/AppModal";
import "./SubAreaBoard.css";
import EditSubAreaForm from "./SubAreaForm/EditSubAreaForm";

// Utility: Safely to number
const toNum = (v) => (typeof v === "number" ? v : parseFloat(v));

// Derive row/col indices if not present
function addRowsCols(subAreas) {
  const ys = Array.from(new Set(subAreas.map((sa) => toNum(sa.positionY)))).sort((a, b) => b - a);
  const xs = Array.from(new Set(subAreas.map((sa) => toNum(sa.positionX)))).sort((a, b) => a - b);
  return subAreas.map((sa) => ({
    ...sa,
    row: ys.indexOf(toNum(sa.positionY)) + 1,
    column: xs.indexOf(toNum(sa.positionX)) + 1,
  }));
}

// Determine if row/column exists in first object
const hasRowsCols = (arr) => arr.length > 0 && "row" in arr[0] && "column" in arr[0];

// Child form with controlled state bubbled up


const SubAreaBoard = ({ subAreas, onSubAreaUpdate }) => {
  if (!subAreas || subAreas.length === 0) {
    return <div className="sub-areas-empty">No sub areas data.</div>;
  }

  const [showModal, setShowModal] = useState(false);
  const [selectedSubArea, setSelectedSubArea] = useState(null);
  // Local draft that receives edits from the form
  const [draft, setDraft] = useState(null);

  // If necessary, enrich data:
  const data = hasRowsCols(subAreas) ? subAreas : addRowsCols(subAreas);

  // Compute grid dimensions
  const maxRow = Math.max(...data.map((item) => item.row));
  const maxCol = Math.max(...data.map((item) => item.column));

  const onSubAreaClick = (item) => {
    setSelectedSubArea(item);
    setDraft({
      name: item.name ?? "",
      code: item.code ?? "",
      maxCapacity: item.maxCapacity ?? null,
    });
    setShowModal(true);
  };

  const onCloseDialogHandler = () => {
    setShowModal(false);
    setSelectedSubArea(null);
    setDraft(null);
  };

  const onSaveDialogHandler = () => {

    // 1) Update the current selected subArea (create a shallow copy with edits)
    // This only updates the selected one locally; parent should own the source of truth.
    const updated = {
      ...selectedSubArea,
      ...draft,
    };

    // 2) Emit event to parent with the updated sub-area
    // Parent can replace the sub-area in its collection and persist to backend.
    onSubAreaUpdate?.(updated);

    // Close modal
    onCloseDialogHandler();
  };

  return (
    <>
      <div
        className="sub-areas-grid"
        style={{
          gridTemplateRows: `repeat(${maxRow}, 1fr)`,
          gridTemplateColumns: `repeat(${maxCol}, 1fr)`,
        }}
      >
        {data.map((item) => (
          <div key={item.code} className="sub-area-tile" onClick={() => onSubAreaClick(item)}>
            <div className="tile-name">{item.name}</div>
            <div className="tile-code">({item.code})</div>
            <div className="tile-width">
              w: {item.width} <span className="tile-unit">{item.unitType}</span>
            </div>
            <div className="tile-height">
              h: {item.height} <span className="tile-unit">{item.unitType}</span>
            </div>
          </div>
        ))}
      </div>

      {/* show a modal to edit the clicked sub area */}
      {showModal && (
        <AppModal
          width={400}
          isModalActive={showModal}
          onHandleModalClose={onCloseDialogHandler}
          onHandleModalSubmit={onSaveDialogHandler}
          title="Edit Sub Area"
          showCancelLabel={true}
          showConfirmLabel={true}
        >
          <EditSubAreaForm
            subArea={selectedSubArea}
            value={draft}
            onChange={(next) => setDraft(next)}
          />
        </AppModal>
      )}
    </>
  );
};

export default SubAreaBoard;
